import {CROP_ASPECT_RATIO} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import type {CropAspectPreset, CropAspectPresetItemsOptions, FixedWindowCropPresetItemsOptions} from '@Components/poster-editor/components/crop-aspect-presets/crop-aspect-presets.types';
import styles from './crop-aspect-presets.module.scss';

/** Locks the crop frame to a ratio, or frees it with `null`. Shared by the desktop side panel and the mobile sheet. */
export const selectCropAspectRatio = (ratio: number | null): void => {
  window.posterEditor?.whiteboard?.mediaCrop.setAspectRatio(ratio);
};

/** The "Original" preset: the whole picture back in the window, no cut and no turn, ratio locked to the picture's. */
export const resetCropToOriginal = (): void => {
  window.posterEditor?.whiteboard?.mediaCrop.resetToOriginal();
};

/** Spins the crop frame to the given angle in degrees. Shared by the desktop side panel and the mobile sheet. */
export const setCropRotation = (angle: number): void => {
  window.posterEditor?.whiteboard?.mediaCrop.setRotation(angle);
};

/**
 * Renders the presets as contextual-menu media controls. Both crop surfaces — the desktop side panel
 * and the mobile bottom sheet — show the same control, so the mapping lives here rather than in each.
 */
export const getCropAspectPresetItems = ({activeAspectRatio, isOriginalSelected, originalAspectRatio, onSelect, onSelectOriginal}: CropAspectPresetItemsOptions): MediaControlsItemProps[] => {
  return getCropAspectPresets(originalAspectRatio).map((preset): MediaControlsItemProps => {
    const isSelected = preset.isOriginal ? isOriginalSelected : !isOriginalSelected && activeAspectRatio === preset.ratio;
    return {
      id: preset.key,
      icon: preset.icon,
      text: preset.label,
      isSelected,
      iconContainerClasses: isSelected ? `${styles.presetTile} ${styles.selectedTile}` : styles.presetTile,
      onClick: (): void => {
        if (preset.isOriginal) {
          onSelectOriginal();
          return;
        }
        onSelect(preset.ratio);
      },
    };
  });
};

/** The one tile a background's crop offers: Original, a reset to the picture upright and centred under the page. */
export const getFixedWindowCropPresetItems = ({onSelectOriginal}: FixedWindowCropPresetItemsOptions): MediaControlsItemProps[] => {
  return [
    {
      id: 'original',
      icon: 'icon-resize',
      text: window.i18next.t('pmwjs_original'),
      isSelected: false,
      iconContainerClasses: styles.presetTile,
      onClick: onSelectOriginal,
    },
  ];
};

/**
 * The crop frame's aspect-ratio presets, in display order.
 *
 * "Original" is only offered once the image's natural ratio is known, since that is its ratio.
 */
const getCropAspectPresets = (originalAspectRatio: number | null): CropAspectPreset[] => {
  const presets: CropAspectPreset[] = [
    {key: 'free', label: window.i18next.t('pmwjs_free'), icon: 'icon-corner', ratio: null, isOriginal: false},
    {key: 'square', label: window.i18next.t('pmwjs_square'), icon: 'icon-square', ratio: CROP_ASPECT_RATIO.SQUARE, isOriginal: false},
  ];

  if (originalAspectRatio !== null) {
    presets.push({key: 'original', label: window.i18next.t('pmwjs_original'), icon: 'icon-resize', ratio: originalAspectRatio, isOriginal: true});
  }

  presets.push(
    {key: '4-3', label: window.i18next.t('pmwjs_crop_ratio_4_3'), icon: 'icon-landscape', ratio: CROP_ASPECT_RATIO.FOUR_THREE, isOriginal: false},
    {key: '16-9', label: window.i18next.t('pmwjs_crop_ratio_16_9'), icon: 'icon-169-aspect-ratio', ratio: CROP_ASPECT_RATIO.SIXTEEN_NINE, isOriginal: false},
    {key: '9-16', label: window.i18next.t('pmwjs_crop_ratio_9_16'), icon: 'icon-916-aspect-ratio', ratio: CROP_ASPECT_RATIO.NINE_SIXTEEN, isOriginal: false}
  );

  return presets;
};
